import { useContext, useEffect, useState } from 'react';
import { FileText, Trash2, User, Plus } from 'react-feather'
import { format, set } from 'date-fns';
import { InfoBlockStatusToken } from '../../ActiveWorkflowsPage/WorkflowInfoBlock';
import { BannerContext } from '../../../InternalApiApp';
import { useRemote, useStorage } from '../../../Utils/Utils';
import { toastDanger } from '../../../components/popups/Toast';
import { dropdownIndicator } from './ExternalAccessUsersList';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import PageGridDivider from '../../Shared/Page/PageGridDivider';
import DetailSectionHeader from '../../Shared/DetailSection/DetailSectionHeader';
import ActionButtonsBanner from '../../Shared/Banner/ActionButtonsBanner';
import DetailField from '../../Shared/DetailSection/DetailField';
import LoadingIcon from '../../Shared/LoadingIcon';
import Table from '../../../components/Table/Table';
import FlexWrapper from '../../FlexWrapper';
import PageContentToggle from '../../../components/Menus/PageContentToggle';
import GenericListHeader from '../../Shared/TablesAndLists/GenericListHeader';
import TextInput from '../../../components/Input/TextInput';
import SearchField from '../../../components/Input/SearchField';
import Tooltip from '../../../components/popups/Tooltip';


const ExternalAccessCompanyPage = ({ match }) => {
    const remote = useRemote()
    const storage = useStorage()
    const externalAccessId = +match.params.externalAccessId

    const { setBannerBreadCrumbs } = useContext(BannerContext);

    const [loading, setLoading] = useState(true)
    const [editMode, setEditMode] = useState(false)
    const [company, setCompany] = useState(null)
    const [status, setStatus] = useState(null)
    const [activationDate, setActivationDate] = useState(null)
    const [templateOptions, setTemplateOptions] = useState([])
    const [templates, setTemplates] = useState([])
    const [originalTemplates, setOriginalTemplates] = useState([])
    const [addedTemplates, setAddedTemplates] = useState([])
    const [deletedTemplates, setDeletedTemplates] = useState([])
    const [domains, setDomains] = useState([])
    const [originalDomains, setOriginalDomains] = useState([])
    const [newDomain, setNewDomain] = useState("")
    const [newDomains, setNewDomains] = useState([])
    const [deletedDomains, setDeletedDomains] = useState([])
    const [editedDomains, setEditedDomains] = useState([])
    const [invalidNewDomain, setInvalidNewDomain] = useState(false)
    const [companyUsers, setCompanyUsers] = useState([])
    const [originalCompanyUsers, setOriginalCompanyUsers] = useState([])
    const [editedUsers, setEditedUsers] = useState([])
    const [roleOptions, setRoleOptions] = useState([])
    const [errorMessage, setErrorMessage] = useState("")
    const [selectedTab, setSelectedTab] = useState('Domains')

    const allSelectableTemplates = templateOptions && templates && templateOptions.filter(template => !templates.some(t => t.id === template.id));
    const noChangesMade = addedTemplates.length === 0 && deletedTemplates.length === 0 && newDomains.length === 0 && deletedDomains.length === 0 && editedDomains.length === 0 && editedUsers.length === 0;

    useEffect(() => {
        storage.getOrFetch('/data/templates/meta').then(fetchedTemplatesMeta => {
            setTemplateOptions(fetchedTemplatesMeta)

            storage.getOrFetch("/roles/find-role/External Editor").then((roles) => {
                const externalRole = roles.filter(r => r.displayName === "External Manager")
                const options = externalRole.length > 0 ? [...externalRole, { displayName: "None", id: null, companyId: externalRole[0].companyId }]
                    .map((role) => { return { label: role.displayName, value: role.id } }) : [];

                setRoleOptions(options)

                refreshData()
            })
        })

    }, [externalAccessId])

    // Generic functions ___________________________________________________
    function refreshData(cancel) {
        remote.get(`/crm/ext/access/lists/users/${externalAccessId}`).then((response) => {
            const fetchedUsers = response.data || []
            setCompanyUsers(fetchedUsers)
            setOriginalCompanyUsers(fetchedUsers)

            remote.get(`/crm/ext/access/${externalAccessId}`).then((response) => {
                const fetchedCompany = response.data || []
                const sortedDomains = fetchedCompany && fetchedCompany.allowedDomains && fetchedCompany?.allowedDomains.length > 0 ? fetchedCompany.allowedDomains.sort((a, b) => a.domain.localeCompare(b.domain)) : []
                const sortedTemplates = fetchedCompany && fetchedCompany.templates && fetchedCompany?.templates.length > 0 ? fetchedCompany.templates.sort((a, b) => a.name.localeCompare(b.name)) : []
                setBannerBreadCrumbs({ tab: 'Companies', param1: { name: fetchedCompany.name, id: externalAccessId } })
                setCompany(fetchedCompany)
                setStatus(fetchedCompany.status)
                setActivationDate(fetchedCompany.activationAt)
                setDomains(sortedDomains)
                setOriginalDomains(sortedDomains)
                setTemplates(sortedTemplates)
                setOriginalTemplates(sortedTemplates)

                if (cancel) cancelChanges(false)
                setLoading(false)

            }).catch(() => {
                setCompany(null)
                if (cancel) cancelChanges(false)
                setLoading(false)
            })
        })
    }
    
    async function saveChanges() {
        let promises = []
        if (addedTemplates.length > 0) {
            const func = remote.post(`/crm/ext/access/domain/${externalAccessId}/templates`, addedTemplates)
            promises.push(func)
        } else promises.push(null)

        if (deletedTemplates.length > 0) {
            const func = remote.delete(`/crm/ext/access/domain/${externalAccessId}/templates`, deletedTemplates)
            promises.push(func)
        } else promises.push(null)

        if (editedDomains.length > 0) {
            const func = remote.put(`/crm/ext/access/domain/${externalAccessId}`, editedDomains)
            promises.push(func)
        } else promises.push(null)

        if (newDomains.length > 0) {
            const func = remote.post(`/crm/ext/access/domain/${externalAccessId}`, newDomains)
            promises.push(func)
        } else promises.push(null)

        if (deletedDomains.length > 0) {
            const func = remote.delete(`/crm/ext/access/domain/${externalAccessId}`, deletedDomains)
            promises.push(func)
        } else promises.push(null)

        if (editedUsers.length > 0) {
            const updatePromises = editedUsers.map((user) => {
                return remote.put(`/crm/users/${user.email}/`, user)
            });
            Promise.all(updatePromises).then(() => {
                setEditedUsers([]);
                setOriginalCompanyUsers(companyUsers);
            }).catch(() => {
                toastDanger("Failed to update users")
                setCompanyUsers(originalCompanyUsers)
                setEditedUsers([]);
            })
        }

        await Promise.all(promises).then(() => refreshData(true)).catch((err) => {
            toastDanger("Failed to save changes")
            console.error("Error saving changes: ", err)
        })
    }

    function cancelChanges(revert) {
        if (revert) {
            setTemplates(originalTemplates)
            setDomains(originalDomains)
            setCompanyUsers(originalCompanyUsers)
        }
        setAddedTemplates([])
        setDeletedTemplates([])
        setNewDomain("")
        setInvalidNewDomain(false)
        setDeletedDomains([])
        setNewDomains([])
        setEditedDomains([])
        setEditMode(false)
    }

    // Templates related functions ___________________________________________________
    function templatesTable(data) {
        return data.map((template, idx) => {
            return (
                <tr key={idx}>
                    <td style={{ height: "100%", cursor: "pointer" }} >
                        <FlexWrapper align={"center"}>
                            {template.name}
                        </FlexWrapper>
                    </td>
                    <td style={{ height: "100%", cursor: "pointer" }} >
                        {editMode ?
                            <Trash2
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeTemplate(template)
                                }}
                                color='#E66780'
                                style={editMode ? { opacity: 1 } : { opacity: 0.5 }}
                                size={20}
                            />
                            : ""
                        }
                    </td>
                </tr>
            );
        });
    }

    function addTemplate(template) {
        let newTemplates = templates.length > 0 ? [...templates, template] : [template]
        const isAdded = originalTemplates.find(t => t.id === template.id) ? false : true;
        if (isAdded) {
            setAddedTemplates(addedTemplates.length > 0 ? [...addedTemplates, template.id] : [template.id])
        }
        deletedTemplates.length > 0 && setDeletedTemplates(deletedTemplates.filter(tId => tId !== template.id))
        setTemplates(newTemplates)
    }

    function removeTemplate(template) {
        let newTemplates = templates.filter(t => t.id !== template.id)
        const isAdded = originalTemplates.find(t => t.id === template.id) ? false : true;
        if (isAdded) {
            setAddedTemplates(addedTemplates.filter(tId => tId !== template.id))
        } else {
            setDeletedTemplates(deletedTemplates.length > 0 ? [...deletedTemplates, template.id] : [template.id])
        }
        setTemplates(newTemplates)
    }

    // Domains related functions ___________________________________________________
    function domainsTable(data) {
        return data.map((domain, idx) => {
            return (
                <tr key={idx}>
                    <td style={{ height: "100%", cursor: "pointer" }} >
                        <FlexWrapper align={"center"}>
                            {editMode ?
                                <TextInput
                                    disabled={!editMode}
                                    // icon={<Edit2 />}
                                    type={"email"}
                                    size="small"
                                    width="m"
                                    placeholder={"Add domain"}
                                    value={domain.domain}
                                    onChange={(val) => handleEditDomain(val.target.value, domain, idx)}
                                    //onKeyDown={(e) => { if (e.key === 'Enter' && domain.domain.length > 0) { handleEditDomain(domain.domain, domain, idx) } }}
                                    helperText={domain.invalid ? "Please enter a valid domain." : ""}
                                    helperTextClass="invalid-message small-top"
                                    invalid={domain.invalid}
                                    inputClassName="input-in-td"
                                />
                                : domain.domain
                            }
                        </FlexWrapper>
                    </td>
                    <td style={{ height: "100%", cursor: "pointer" }} >
                        {editMode ?
                            <Trash2
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeDomain(domain)
                                }}
                                color='#E66780'
                                style={editMode ? { opacity: 1 } : { opacity: 0.5 }}
                                size={20}
                            /> : ""
                        }
                    </td>
                </tr>
            );
        });
    }

    function removeDomain(domain) {
        const isNewDomain = newDomains.find(d => d.domain === domain.domain) ? true : false;
        if (isNewDomain) {
            let updatedNewDomains = newDomains.filter(d => d.domain !== domain.domain)
            setNewDomains(updatedNewDomains)
        } else {
            const filteredEditedDomains = editedDomains.filter(ed => ed.domainId !== domain.domainId)
            if (editedDomains.length !== filteredEditedDomains.length) {
                const originalDomainToBeDeleted = originalDomains.find(d => d.domainId === domain.domainId)
                setEditedDomains(filteredEditedDomains)
                setDeletedDomains(deletedDomains.length > 0 ? [...deletedDomains, originalDomainToBeDeleted] : [originalDomainToBeDeleted])
            } else {
                setDeletedDomains(deletedDomains.length > 0 ? [...deletedDomains, domain] : [domain])
            }
        }
        let filteredDomains = domains.filter(d => d.domainId !== domain.domainId)
        setDomains(filteredDomains)
    }

    function handleNewDomain(domainName) {
        /* Validates domain names including subdomains and internationalized domain names.
         * <li> Supports domains like: example.com, mail.google.com, api.staging.example.co.uk
         * <li> Ref: RFC 1035, RFC 1123, RFC 2181
         * <li> Label length: 1-63 characters (RFC 1035)
         * <li> Total domain length: 253 characters maximum (RFC 2181)
         * <li> Character set: Letters, digits, hyphens (RFC 1035/1123)
         * <li> Hyphen rules: Cannot start or end with hyphen (RFC 1035)
         * <li> Minimum structure: At least two labels separated by dots
         * <li> TLD rules: Last label (TLD) must be at least 2 characters and contain only letters (RFC 1035)
         * <li> It is not case-sensitive
         */
        const isValidDomain = validateDomainName(domainName);
        const duplicateDomain = domains.find(d => d.domain === domainName);
        if (isValidDomain && !duplicateDomain) {
            let newDomainObj = { domain: domainName }
            let updatedDomains = domains.length > 0 ? [...domains, newDomainObj] : [newDomainObj]
            setDomains(updatedDomains)
            setNewDomain("")
            setNewDomains(newDomains.length > 0 ? [...newDomains, newDomainObj] : [newDomainObj])
            setInvalidNewDomain(false)
        } else {
            setErrorMessage(duplicateDomain ? "Domain already exists." : "")
            setInvalidNewDomain(true)
        }

    }

    function handleEditDomain(domainName, domainObj, idx) {
        const isValidDomain = validateDomainName(domainName);
        let newlyCreated = {}
        const updatedDomains = domains.map((d, i) => {
            if (idx === i) {
                const updatedObj = { domain: domainName }
                if (domainObj.domainId) {
                    updatedObj.domainId = domainObj.domainId
                    updatedObj.groupId = domainObj.groupId;
                    const hasChanged = originalDomains[i].domain !== domainName;
                    if (hasChanged) {
                        const currentIndex = editedDomains.length > 0 ? editedDomains.findIndex(ed => ed.domainId === domainObj.domainId) : -1;
                        if (currentIndex !== -1) {
                            const updatedArr = [...editedDomains]
                            updatedArr[currentIndex] = updatedObj;
                            setEditedDomains(updatedArr);
                        } else {
                            setEditedDomains([updatedObj]);
                        }
                    } else {
                        setEditedDomains(editedDomains.filter(ed => ed.domainId !== domainObj.domainId))
                    }
                } else {
                    newlyCreated[i] = updatedObj;
                };
                if (!isValidDomain) updatedObj.invalid = true;
                return updatedObj;
            } else {
                if (d && !d.domainId && newlyCreated[i] === undefined) {
                    newlyCreated[i] = d;
                }
                return d;
            }
        })
        const replacedNewlyCreated = Object.values(newlyCreated)
        setNewDomains(replacedNewlyCreated)
        setDomains(updatedDomains)
    }

    // Domain validation function, mirroring from backend validation logic
    function validateDomainName(domain) {
        if (!domain || typeof domain !== 'string') {
            return false;
        }

        // Remove leading/trailing whitespace
        domain = domain.trim().toLowerCase();

        // Check if domain ends with a dot (FQDN) and remove it
        if (domain.endsWith('.')) {
            domain = domain.substring(0, domain.length - 1);
        }

        // Check overall length (max 253 characters for FQDN)
        if (domain.length > 253 || domain.length === 0) {
            return false;
        }

        // Domain must have at least one dot
        if (!domain.includes('.')) {
            return false;
        }

        // Split into labels (parts separated by dots)
        const labels = domain.split('.');

        // Must have at least 2 labels (e.g., "example.com")
        if (labels.length < 2) {
            return false;
        }

        // Validate each label
        for (const label of labels) {
            if (!isValidDomainLabel(label)) {
                return false;
            }
        }

        // Last label (TLD) must be at least 2 characters and contain only letters
        const tld = labels[labels.length - 1];
        if (tld.length < 2 || !/^[a-z]+$/.test(tld)) {
            return false;
        }

        return true;
    }

    function isValidDomainLabel(label) {
        // Label cannot be empty
        if (!label || label.length === 0) {
            return false;
        }

        // Label length must be between 1 and 63 characters
        if (label.length > 63) {
            return false;
        }

        // Label cannot start or end with a hyphen
        if (label.startsWith('-') || label.endsWith('-')) {
            return false;
        }

        // Label must contain only alphanumeric characters and hyphens
        if (!/^[a-z0-9-]+$/.test(label)) {
            return false;
        }

        return true;
    }

    function handleAccessChange(users, email, newValue) {
        const updatedUsers = users.map((u, index) => {
            if (u.email === email) {
                const updatedUser = { ...u, roleId: newValue.value, roleDTO: !newValue.value ? null : { displayName: newValue.label, roleId: newValue.value } };
                if (originalCompanyUsers[index].roleId !== updatedUser.roleId) {
                    setEditedUsers(editedUsers ? [...editedUsers, updatedUser] : [updatedUser]);
                } else {
                    setEditedUsers(editedUsers.filter(eu => eu.email !== email));
                }
                return updatedUser;
            }
            return u;
        })
        setCompanyUsers(updatedUsers);
    }

    // Users related functions ___________________________________________________
    function usersTable(data) {
        return data.map((user, idx) => {
            const { email, name, externalAccessName, roleId } = user
            const access = roleId ? "External Manager" : "None"
            return (
                <tr key={idx}>
                    <td style={{ paddingLeft: '0.5rem' }}>{name || '-'}</td>
                    <td>{externalAccessName || '-'}</td>
                    <td>{editMode
                        ? <SearchField
                            value={roleId ? roleOptions[0] : roleOptions[1]}
                            options={roleOptions}
                            onChange={(newValue) => handleAccessChange(data, email, newValue)}
                            size='small'
                            indicatorSeparator={dropdownIndicator}
                        />
                        : access}</td>
                </tr>
            );
        });
    }

    const labels = [
        { label: 'Domains', active: selectedTab === 'Domains', onClick: () => setSelectedTab('Domains') },
        { label: 'Templates', active: selectedTab === 'Templates', onClick: () => setSelectedTab('Templates') },
        { label: 'Users', active: selectedTab === 'Users', onClick: () => setSelectedTab('Users') }
    ]

    return (
        <Page noBanner noOverflownY>
            {loading ?
                <PageContentBodyGrid rowGap="none" gridColCount="12" subGrid paddingTop='1.5rem'>
                    <LoadingIcon />
                </PageContentBodyGrid> :
                <PageContentBodyGrid rowGap="l" gridColCount="12" subGrid paddingTop='1.5rem'>
                    <PageGridItem gap='l' direction='col' col="1 / span 12" >
                        <ActionButtonsBanner
                            hideBackButton
                            editMode={editMode}
                            edit={{ onClick: () => setEditMode(true) }}
                            save={{ onClick: () => saveChanges(), disabled: noChangesMade }}
                            cancel={{ onClick: () => cancelChanges(true) }}
                        />
                        <PageGridDivider />
                    </PageGridItem>
                    <PageGridItem container col="1 / span 6" gap='xl' direction='col'>
                        <DetailSectionHeader header='Company Details' icon={<FileText />} />
                        <PageContentBodyGrid rowGap="xl" gridColCount="4" subGrid>
                            <PageGridItem col="1 / span 2">
                                <DetailField
                                    bold
                                    onChange={(e) => null}
                                    editMode={editMode}
                                    disabled={editMode}
                                    label="Name"
                                    value={company?.name || '-'}
                                />
                            </PageGridItem>
                            <PageGridItem col="3 / span 2">
                                <DetailField
                                    onChange={(e) => null}
                                    editMode={editMode}
                                    disabled={editMode}
                                    label="Activation Date"
                                    value={activationDate ? format(new Date(activationDate), 'dd/MM/yyyy') : '-'}
                                />
                            </PageGridItem>
                            <PageGridItem col="1 / span 2" >
                                <FlexWrapper direction="column" gap="xs" align="start">
                                    <label className='colour-text-label-grey'>Status</label>
                                    <Tooltip disabled={status === "ACTIVE"} label="To activate this company, please add at least one domain."><InfoBlockStatusToken status={status} /></Tooltip>
                                </FlexWrapper>
                            </PageGridItem>

                        </PageContentBodyGrid>
                    </PageGridItem>
                    <PageGridDivider col="1 / span 6" />
                    <PageGridItem container col="1 / span 6">
                        <PageContentToggle labels={labels} />
                    </PageGridItem>
                    {selectedTab === 'Domains' && <PageGridItem col="1 / span 6" gap='l' direction='col'>
                        <FlexWrapper justify={"space-between"} align="start">
                            <DetailSectionHeader header={`Domains (${domains.length})`} icon={<FileText />} />
                            {editMode && <TextInput
                                disabled={!editMode}
                                icon={<Plus style={invalidNewDomain ? { color: '#A0001E' } : null} onClick={() => newDomain.length > 0 ? handleNewDomain(newDomain) : null} />}
                                iconstyle={{ left: 'calc(240px - 1.5rem)', cursor: 'pointer', bottom: invalidNewDomain ? '1.5rem' : null }}
                                inputstyle={{ paddingLeft: '0.5rem' }}
                                type={"email"}
                                size="small"
                                width="m"
                                placeholder={"Add domain"}
                                value={newDomain}
                                onChange={(val) => setNewDomain(val.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && newDomain.length > 0) { handleNewDomain(newDomain) } }}
                                helperText={invalidNewDomain ? errorMessage ? errorMessage : "Please enter a valid domain." : ""}
                                helperTextClass="invalid-message small-top"
                                invalid={invalidNewDomain}
                            />}
                        </FlexWrapper>
                        <Table
                            suggestionMessage
                            colWidths={['auto', '4rem']}
                            headers={["Name", ""]}
                            dataMap={domainsTable(domains)}
                        />
                    </PageGridItem>
                    }
                    {selectedTab === 'Templates' && <PageGridItem col="1 / span 6" gap='l' direction='col'>
                        <FlexWrapper justify={"space-between"} align="start">
                            {editMode ? <GenericListHeader
                                onSearchSelect={(list) => editMode && addTemplate(list)}
                                header={"Templates"}
                                headerOptions={[{ label: "Templates", value: "Templates" }]}
                                onHeaderOptionSelect={null}
                                addableLists={allSelectableTemplates.map((template) => ({ value: template, label: template.name }))}
                                userLists={templates}
                                hideFilterInput
                                icon={<FileText />}
                                disableAddInput={!editMode}
                                placeholder='Add template'
                                indicatorSeparator={dropdownIndicator}
                            /> : <DetailSectionHeader header={`Templates (${templates.length})`} icon={<FileText />} />
                            }
                        </FlexWrapper>
                        <Table
                            suggestionMessage
                            colWidths={['auto', '4rem']}
                            headers={["Name", ""]}
                            dataMap={templatesTable(templates)}
                        />
                    </PageGridItem>
                    }
                    {selectedTab === 'Users' && <PageGridItem col="1 / span 6" gap='l' direction='col'>
                        <FlexWrapper justify={"space-between"} align="start">
                            <DetailSectionHeader header={`Users (${templates.length})`} icon={<User />} />
                        </FlexWrapper>
                        <Table
                            suggestionMessage
                            suggestionMessageText="Users will appear here automatically once they login."
                            colWidths={['35%', '30%', '35%']}
                            headers={["Name", "Company", "Access"]}
                            dataMap={usersTable(companyUsers)}
                        />
                    </PageGridItem>
                    }
                </PageContentBodyGrid >
            }
        </Page >
    )
}

export default ExternalAccessCompanyPage;